import { Help } from '@shared/constants/help';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { userSelectors } from '@store/selectors/user.selectors';
import { StoreState } from '@store/store.state';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/ui-components/icon/icon.component';
import { Footer } from '@shared/constants/footer';
import { CustomLinkDirective } from '@shared/directives/custom-link/custom-link.directive';
import { PortugalHelp } from '@shared/constants/portugal-help';
import { filter, map, startWith } from 'rxjs';

@Component({
  selector: 'ac-footer-links',
  standalone: true,
  templateUrl: './footer-links.component.html',
  styleUrls: ['./footer-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TranslateModule, RouterLink, CustomLinkDirective],
})
export class FooterLinksComponent {
  private readonly _router = inject(Router);
  protected readonly _emailSig = this._store.selectSignal(userSelectors.helpEmail);
  protected readonly _userSig = this._store.selectSignal(userSelectors.authUser);
  protected readonly _countrySig = this._store.selectSignal(userSelectors.country);
  protected readonly _isEnergyRehabilitationModeSig = this._store.selectSignal(userSelectors.isEnergyRehabilitationMode);
  protected readonly _currentUrlSig = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this._router.url),
      startWith(this._router.url)
    ),
    { initialValue: this._router.url }
  );
  protected readonly _spanishPhoneNumber: string = Help.PHONE;
  protected readonly _portugueseComercialPhoneNumber: string = PortugalHelp.PHONE_COMERCIAL;
  protected readonly _portugueseMeterReadingPhoneNumber: string = PortugalHelp.PHONE_METER_READING;
  protected readonly _portugueseOutagesPhoneNumber: string = PortugalHelp.PHONE;

  protected readonly _footerConstants = {
    acciona: Footer.ACCIONA,
    we: Footer.WE,
    solutions: Footer.SOLUTIONS,
    solutionsPt: Footer.SOLUTIONS_PT,
    currentNews: Footer.CURRENT_NEWS,
  };

  protected readonly _isRehabilitationContextSig = computed(() => {
    const currentUrlTree = this._router.parseUrl(this._currentUrlSig());
    const currentPath = currentUrlTree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '';
    const isRehabilitationFromQuery = currentUrlTree.queryParams['rehab'] === 'true';
    const isRehabilitationRoute = currentPath === 'consumption-invoices' || currentPath.startsWith('consumption-invoices/');

    return this._isEnergyRehabilitationModeSig() || isRehabilitationRoute || isRehabilitationFromQuery;
  });

  constructor(private readonly _store: Store<StoreState>) {}
}
